/** The Trips schedule axis (plan "Page anatomy" → Trips panel; DESIGN.md §5 "Time axis"):
 *  one horizontal axis around now with the 1 px dashed navy "now" line, every trip a bar
 *  from start to end in packed lanes — past navy 35 % fill, active solid navy, future white
 *  with a navy outline, cancelled a dashed disabled-grey outline; a zero-length trip the
 *  3×14 px booked tick. Under a staff trip two 2 px lines: solid the guide-app window
 *  (dashed when the guide's cache does not hold the booking), dotted the chat window.
 *  Ticks are dates in the dominant zone, named ("Axis: Europe/Rome"), as many as the plot's
 *  width holds without two labels touching. The plot fills the card at any width (only the
 *  account band scrolls sideways). What the loaded pages leave out of the window is
 *  hatched, with Load earlier / Load later.
 *
 *  Keyboard (the `TimeAxis` pattern): the bars are one tab stop; ←/→ move between trips,
 *  Home/End to the first/last; Enter (or a click) focuses the trip's row in the list
 *  below, which is the axis's text equivalent (`aria-describedby`). */
import { alpha, Box, Button, ButtonBase, Tooltip, Typography, type Theme } from '@mui/material';
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';

import type { BookingListItem } from '@/api/bookings';
import { chatStatus, guideAppStatus } from '@/features/bookings/tripStatus';
import { useElementWidth } from '@/features/records/page/recordPage';
import { TRIP_PHASE_LABEL } from '@/features/records/time/TripPhaseLabel';
import { hatchSx, NOW_RULE_SX, timeMarkSx } from '@/features/records/timeline/marks';
import { axisPct, axisX } from '@/features/records/timeline/scale';

import {
  axisLayout,
  axisTicks,
  isCancelled,
  isStaffRow,
  tickLabelPx,
  tripWhen,
  type AxisWindow,
  type PlacedTrip,
  type TripGroupId,
} from './tripsModel';

const LANE_PX = 26;
const NOW_LABEL_PX = 16;
const OVERFLOW_PX = 18;
const TICKS_PX = 20;
const BAR_TOP = 4;
const BAR_PX = 10;
const LEGEND_PX = 24;
const ZONE_PX = 20;
const GAP_PX = 6;

/** The axis block with one lane — the height its loading skeleton reserves. */
export const AXIS_SKELETON_PX = ZONE_PX + GAP_PX + NOW_LABEL_PX + LANE_PX + 1 + TICKS_PX + GAP_PX + LEGEND_PX;

const FOCUS_RING = { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } as const;

function barSx(p: PlacedTrip) {
  return (t: Theme) => {
    const navy = t.palette.primary.main;
    if (isCancelled(p.row)) return { border: `1.5px dashed ${t.palette.text.disabled}`, bgcolor: 'transparent' };
    switch (p.group) {
      case 'past':
        return { bgcolor: alpha(navy, 0.35), border: `1px solid ${alpha(navy, 0.35)}` };
      case 'active':
        return { bgcolor: navy };
      default:
        return { bgcolor: t.palette.background.paper, border: `1.5px solid ${navy}` };
    }
  };
}

/** The bar's words: the tour, when, its phase or state, and the guide-app and chat lines
 *  with why — the same words as the rows and the bookings grid. */
function tripLabel(p: PlacedTrip, axisZone: string, now: Date): string[] {
  const when = tripWhen(p.row, axisZone, now);
  const state = isCancelled(p.row) ? p.row.booking_state_display : TRIP_PHASE_LABEL[p.group];
  const lines = [p.row.tour.name, when.main, state];
  if (isStaffRow(p.row) && p.row.staff_visibility) lines.push(guideAppStatus(p.row.staff_visibility, p.row.timezone, now).text);
  if (p.row.chat) lines.push(chatStatus(p.row.chat, p.row.timezone).text);
  return lines;
}

export interface ScheduleAxisProps {
  groups: { group: TripGroupId; rows: BookingListItem[] }[];
  window: AxisWindow;
  zone: string;
  now: Date;
  /** Hatched from the left edge to this x (0–1): earlier trips are not loaded. */
  earlier: number | null;
  later: number | null;
  onLoadEarlier: () => void;
  onLoadLater: () => void;
  loadingMore: boolean;
  /** A trip was chosen (Enter or click): focus its row. */
  onActivate: (bookingId: number) => void;
  /** The list below — the axis's text equivalent. */
  describedBy: string;
}

export function ScheduleAxis({
  groups,
  window: win,
  zone,
  now,
  earlier,
  later,
  onLoadEarlier,
  onLoadLater,
  loadingMore,
  onActivate,
  describedBy,
}: ScheduleAxisProps) {
  const layout = useMemo(() => axisLayout(groups, win), [groups, win]);
  const [plotRef, plotWidth] = useElementWidth<HTMLDivElement>();
  const ticks = useMemo(() => axisTicks(win, zone, plotWidth), [win, zone, plotWidth]);
  const nowX = axisX(now.getTime(), null, [win.from, win.to]);
  const lanes = Math.max(1, layout.lanes);
  const plotPx = NOW_LABEL_PX + lanes * LANE_PX + (layout.overflow.length ? OVERFLOW_PX : 0);

  // Roving focus over the bars, in time order.
  const ordered = useMemo(() => [...layout.trips].sort((a, b) => a.x0 - b.x0 || a.lane - b.lane), [layout.trips]);
  const [active, setActive] = useState(0);
  const refs = useRef<(HTMLElement | null)[]>([]);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (ordered.length === 0) return;
    const last = ordered.length - 1;
    const next =
      e.key === 'ArrowRight' ? Math.min(last, active + 1)
      : e.key === 'ArrowLeft' ? Math.max(0, active - 1)
      : e.key === 'Home' ? 0
      : e.key === 'End' ? last
      : null;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
    refs.current[next]?.focus();
  };
  const current = Math.min(active, Math.max(0, ordered.length - 1));

  return (
    <Box sx={{ display: 'grid', gap: `${GAP_PX}px`, minWidth: 0 }} data-schedule-axis>
      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right', lineHeight: `${ZONE_PX}px` }} data-axis-zone>
        Axis: {zone}
      </Typography>
      <Box>
        <Box ref={plotRef} sx={{ position: 'relative', minWidth: 0 }} data-axis-plot>
          {/* The plot: hatching, now, lanes. */}
          <Box sx={{ position: 'relative', height: plotPx, borderBottom: 1, borderColor: 'divider' }}>
            {earlier !== null && earlier > 0 && (
              <Box
                aria-hidden
                data-unloaded="earlier"
                sx={(t) => ({ position: 'absolute', left: 0, width: axisPct(earlier), top: 0, bottom: 0, borderRadius: '3px', ...hatchSx(t) })}
              />
            )}
            {later !== null && later < 1 && (
              <Box
                aria-hidden
                data-unloaded="later"
                sx={(t) => ({ position: 'absolute', left: axisPct(later), right: 0, top: 0, bottom: 0, borderRadius: '3px', ...hatchSx(t) })}
              />
            )}
            <Box aria-hidden data-now sx={{ position: 'absolute', left: axisPct(nowX), top: 2, bottom: 0, ...NOW_RULE_SX }}>
              <Typography
                component="span"
                sx={{ position: 'absolute', left: 4, top: 0, fontSize: 10.5, fontWeight: 700, lineHeight: 1, color: 'primary.main' }}
              >
                now
              </Typography>
            </Box>
            <Box
              role="group"
              aria-label={`Schedule: ${ordered.length} ${ordered.length === 1 ? 'trip' : 'trips'} — ← and → move between them, Enter opens its row`}
              aria-describedby={describedBy}
              onKeyDown={onKeyDown}
              sx={{ position: 'absolute', inset: `${NOW_LABEL_PX}px 0 0 0` }}
            >
              {ordered.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', left: 8, top: 4 }}>
                  No trips in this range
                </Typography>
              )}
              {ordered.map((p, i) => (
                <TripMark
                  key={p.row.id}
                  trip={p}
                  label={tripLabel(p, zone, now)}
                  tabIndex={i === current ? 0 : -1}
                  register={(el) => {
                    refs.current[i] = el;
                  }}
                  onFocus={() => setActive(i)}
                  onActivate={() => onActivate(p.row.id)}
                />
              ))}
              {layout.overflow.map((o) => (
                <Typography
                  key={o.x}
                  variant="caption"
                  data-overflow={o.count}
                  sx={{
                    position: 'absolute',
                    top: lanes * LANE_PX,
                    left: axisPct(o.x),
                    transform: o.x > 0.9 ? 'translateX(-100%)' : 'none',
                    color: 'text.secondary',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    lineHeight: `${OVERFLOW_PX}px`,
                  }}
                >
                  +{o.count} overlapping
                </Typography>
              ))}
            </Box>
          </Box>
          {/* Date ticks in the axis zone. */}
          <Box aria-hidden sx={{ position: 'relative', height: TICKS_PX }}>
            {ticks.map((t) => (
              <Box key={t.x} sx={{ position: 'absolute', left: axisPct(t.x), top: 0 }} data-axis-tick>
                <Box sx={{ width: '1px', height: 4, bgcolor: 'divider' }} />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  sx={{
                    position: 'absolute',
                    top: 2,
                    transform: tickShift(t.x, t.label, plotWidth),
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1.6,
                  }}
                >
                  {t.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', minHeight: LEGEND_PX }}>
        {layout.staffLines && <Legend uncached={layout.trips.some((t) => t.app?.dashed)} />}
        <Box sx={{ flex: 1 }} />
        {earlier !== null && (
          <Button size="small" onClick={onLoadEarlier} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load earlier'}
          </Button>
        )}
        {later !== null && (
          <Button size="small" onClick={onLoadLater} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load later'}
          </Button>
        )}
      </Box>
    </Box>
  );
}

/** A tick label centred on its tick, except at the plot's ends, where it would hang out of
 *  the card: there it starts at the tick (left end) or ends at it (right end). */
function tickShift(x: number, label: string, widthPx: number): string {
  if (widthPx <= 0) return x >= 0.97 ? 'translateX(-100%)' : 'translateX(-50%)';
  const half = tickLabelPx(label) / 2;
  if (x * widthPx < half) return 'none';
  if ((1 - x) * widthPx < half) return 'translateX(-100%)';
  return 'translateX(-50%)';
}

function TripMark({
  trip: p,
  label,
  tabIndex,
  register,
  onFocus,
  onActivate,
}: {
  trip: PlacedTrip;
  label: string[];
  tabIndex: number;
  register: (el: HTMLElement | null) => void;
  onFocus: () => void;
  onActivate: () => void;
}) {
  const top = p.lane * LANE_PX;
  const width = Math.max(0, p.x1 - p.x0);
  const cancelled = isCancelled(p.row);
  return (
    <>
      <Tooltip
        title={
          <Box sx={{ display: 'grid', gap: 0.25 }}>
            {label.map((l, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i}>{l}</span>
            ))}
          </Box>
        }
        describeChild
        placement="top"
      >
        <ButtonBase
          ref={register}
          tabIndex={tabIndex}
          onFocus={onFocus}
          onClick={onActivate}
          aria-label={label.join(' · ')}
          data-trip-mark={p.row.id}
          data-phase={p.group}
          data-cancelled={cancelled || undefined}
          sx={
            p.tick
              ? {
                  position: 'absolute',
                  left: axisPct(p.x0),
                  top: top + 1,
                  width: 12,
                  height: 16,
                  transform: 'translateX(-50%)',
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: '3px',
                  '&.Mui-focusVisible': FOCUS_RING,
                }
              : (t: Theme) => ({
                  position: 'absolute',
                  left: axisPct(p.x0),
                  width: `max(6px, ${axisPct(width)})`,
                  top: top + BAR_TOP,
                  height: BAR_PX,
                  borderRadius: '3px',
                  boxSizing: 'border-box',
                  ...barSx(p)(t),
                  '&.Mui-focusVisible': FOCUS_RING,
                  '&:hover': { zIndex: 1, boxShadow: `0 0 0 1px ${t.palette.primary.main}` },
                })
          }
        >
          {p.tick && (
            <Box
              component="span"
              aria-hidden
              sx={(t) => ({ ...timeMarkSx('booked')(t), ...(cancelled ? { bgcolor: t.palette.text.disabled } : {}) })}
            />
          )}
        </ButtonBase>
      </Tooltip>
      {p.app && (
        <Box
          aria-hidden
          data-app-line={p.app.dashed ? 'dashed' : 'solid'}
          sx={(t) => ({
            position: 'absolute',
            left: axisPct(p.app!.x0),
            width: `max(4px, ${axisPct(Math.max(0, p.app!.x1 - p.app!.x0))})`,
            top: top + BAR_TOP + BAR_PX + 3,
            borderTop: p.app!.dashed
              ? `2px dashed ${alpha(t.palette.primary.main, 0.6)}`
              : `2px solid ${t.palette.primary.main}`,
          })}
        />
      )}
      {p.chat && (
        <Box
          aria-hidden
          data-chat-line
          sx={{
            position: 'absolute',
            left: axisPct(p.chat.x0),
            width: `max(4px, ${axisPct(Math.max(0, p.chat.x1 - p.chat.x0))})`,
            top: top + BAR_TOP + BAR_PX + 7,
            borderTop: '2px dotted',
            borderTopColor: 'primary.main',
          }}
        />
      )}
    </>
  );
}

/** Names the lines under the staff trips: "— guide app  ··· chat", and the dashed app line
 *  when one is drawn. */
function Legend({ uncached }: { uncached: boolean }) {
  const item = (style: 'solid' | 'dotted', label: string) => (
    <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box component="span" aria-hidden sx={{ width: 18, borderTop: `2px ${style}`, borderTopColor: 'primary.main' }} />
      {label}
    </Box>
  );
  return (
    <Box
      component="ul"
      aria-label="Legend"
      sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: '4px 16px', typography: 'caption', color: 'text.secondary' }}
    >
      {item('solid', 'guide app')}
      {item('dotted', 'chat')}
      {uncached && (
        <Box component="li" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="span" aria-hidden sx={(t) => ({ width: 18, borderTop: `2px dashed ${alpha(t.palette.primary.main, 0.6)}` })} />
          guide app, not in the guide’s cache
        </Box>
      )}
    </Box>
  );
}
